import { useContext } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { ButtonBase, Grid, Typography, useMediaQuery, useTheme } from '@mui/material'

import UseCaseIcon from '../../../components/use-case-icons'
import { NavContext } from '../../../providers/nav-provider'
import useIsMobile from '../../../utils/hooks/useIsMobile'
import getNodeTypeMatrix from '../../../utils/nodetype-matrix'
import { getAlertIssueType } from '../helpers'
import useStyles from '../index.styles'

import IssueIcons from './issue-icons'
import StatusChip from './status-chip'

const DeviceItem = ({ device }: { device: IDeviceType }) => {
  const classes = useStyles
  const navigate = useNavigate()
  const location = useLocation()
  const { deviceId, folderId: folderIdParam } = useParams<{ deviceId: string; folderId: string }>()
  const selectedFolderId = folderIdParam ? Number(folderIdParam) : undefined

  const theme = useTheme()
  const { sidenavOpen } = useContext(NavContext)
  const isSmallDesktop = useMediaQuery(theme.breakpoints.down('lg'))
  const isMobile = useIsMobile()
  const isSmallScreen = (sidenavOpen && isSmallDesktop) || isMobile
  const deviceLocation = device.node.area || device.node.metadata.device_location
  const deviceLocationDetails = [device.node.floor ? `Floor ${device.node.floor}` : '', device.node.metadata.device_location_note].filter(Boolean).join(' · ')

  const urgentAlerts = device.alerts.filter((alert) => alert.tags.includes('urgent'))
  const nonUrgentAlerts = device.alerts.filter((alert) => !alert.tags.includes('urgent'))
  // Create a separate array of non urgent alert issue types present, and then remove the duplicates
  const nonUrgentAlertIssueTypes = Array.from(new Set(nonUrgentAlerts.map((alert) => getAlertIssueType(alert))))

  let deviceIssueStatus = 'none' as 'none' | 'urgent-alert-action-needed' | 'urgent-alert-on-it' | 'urgent-alert-ignored'
  if (urgentAlerts.some((alert) => alert.responseHistory.length === 0)) {
    deviceIssueStatus = 'urgent-alert-action-needed'
  } else if (urgentAlerts.some((alert) => alert.responseHistory.some((response) => response.response === 'ack'))) {
    deviceIssueStatus = 'urgent-alert-on-it'
  } else if (urgentAlerts.some((alert) => alert.responseHistory.some((response) => response.response === 'test'))) {
    deviceIssueStatus = 'urgent-alert-ignored'
  }
  const urgentStatusChip = deviceIssueStatus === 'none' ? null : deviceIssueStatus

  let slimJoiningPeriod = 'online'
  if (device.node.nodeTypeName === 'leak5s') {
    const hasNeverCheckedIn = !device.lastCheckIn || new Date(device.lastCheckIn ?? '').valueOf() === 0
    const isAudibleOnly = !hasNeverCheckedIn && new Date(device.lastCheckIn ?? '').valueOf() < Date.now() - 48 * 60 * 60 * 1000
    if (hasNeverCheckedIn) slimJoiningPeriod = 'connecting'
    if (isAudibleOnly) slimJoiningPeriod = 'audible-only'
  }
  const shouldShowIssueIcons = !urgentStatusChip
  const slimStatusChip = !urgentStatusChip && slimJoiningPeriod !== 'online' ? (`slim-${slimJoiningPeriod}` as 'slim-connecting' | 'slim-audible-only') : null

  const issueIcons = (
    <Grid container={true} sx={classes.deviceItemIssueIconsRow}>
      <IssueIcons
        nodeTypeName={device.node.nodeTypeName ?? ''}
        nonUrgentAlertIssueTypes={nonUrgentAlertIssueTypes}
        isLowBattery={device.hasLowBattery}
        hasLowSignal={device.hasLowSignal}
        isCommunicating={!device.isOffline}
        temperature={device.currentValues?.temp?.value as number | null | undefined}
      />
    </Grid>
  )

  return (
    <Grid
      key={device.node.id}
      container={true}
      size={isSmallScreen ? 6 : 12}
      sx={classes.deviceItemContainer(isSmallScreen, theme, deviceId === String(device.node.id))}
    >
      <ButtonBase
        component={Grid}
        container={true}
        sx={classes.deviceItemInfoContainer(isSmallScreen)}
        onClick={() =>
          navigate(
            {
              pathname: selectedFolderId ? `/dashboard/${selectedFolderId}/devices/${device.node.id}` : '/dashboard',
              hash: location.hash,
              search: location.search,
            },
            { state: location.state }
          )
        }
      >
        <Grid>
          <UseCaseIcon useCase={device.deviceUseCase} />
        </Grid>
        <Grid container={true} sx={classes.deviceItemTextContainer}>
          <Typography variant="caption" sx={classes.deviceItemNodeTypeText}>
            {getNodeTypeMatrix[device.node.nodeTypeName ?? '']}
          </Typography>
          <Typography variant="body2" sx={classes.deviceItemLocationText}>
            {deviceLocation}
          </Typography>
          {!isSmallScreen && <Typography variant="caption">{deviceLocationDetails}</Typography>}
        </Grid>
      </ButtonBase>
      <Grid container={true} sx={classes.deviceItemStatusColumn}>
        <Grid container={true} sx={classes.deviceItemStatusRow(isSmallScreen)}>
          {slimStatusChip && <StatusChip status={slimStatusChip} />}
          {urgentStatusChip ? (
            <Grid container={true} sx={classes.deviceChipContainer(isMobile)}>
              <StatusChip status={urgentStatusChip} deviceId={device.node.id} />
            </Grid>
          ) : shouldShowIssueIcons ? (
            issueIcons
          ) : null}
        </Grid>
      </Grid>
    </Grid>
  )
}

export default DeviceItem
